import { Link } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import logo from "@/assets/logo.svg";
import { useAuth, useAuthActions } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";

const COPYRIGHT_NOTICE = `All material appearing on the RossCreations website (“content”) is protected by copyright under U.S. Copyright laws and is the property of RossCreations or the party credited as the provider of the content. You may not copy, reproduce, distribute, publish, display, perform, modify, create derivative works, transmit, or in any way exploit any such content, nor may you distribute any part of this content over any network, including a local area network, sell or offer it for sale, or use such content to construct any kind of database. You may not alter or remove any copyright or other notice from copies of the content on RossCreations' website. Copying or storing any content except as provided above is expressly prohibited without prior written permission of the company or the copyright holder identified in the individual content's copyright notice. For permission to use the content on the RossCreations website, please contact members@rosscreations.com .`;

export function Footer() {
  const { signedIn } = useAuth();
  const { signOut } = useAuthActions();
  const dev = useDevState();
  const navigate = useNavigate();

  const isLoggedIn = signedIn || dev.isSignedIn;

  const handleSignOut = async () => {
    if (dev.state !== "real") {
      dev.setState("logged-out");
      toast.success("Signed out (dev state)");
      navigate({ to: "/" });
      return;
    }
    const result = await signOut();
    if ("success" in result) {
      toast.success("Signed out");
      navigate({ to: "/" });
    } else {
      toast.error(result.error);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer className="mt-24 border-t border-border bg-card text-card-foreground">
      <div className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-12 md:grid-cols-[1.4fr_3fr]">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-flex items-center" aria-label="Real Pranks home">
              <img src={logo} alt="Real Pranks" className="h-10 w-auto" />
            </Link>
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              A new prank video every week. Stay subscribed, catch every drop.
            </p>
          </div>

          {/* Link columns */}
          <div className="grid gap-10 sm:grid-cols-3">
            <FooterColumn title="Account">
              <FooterLink to="/membership">Plans &amp; pricing</FooterLink>
              {isLoggedIn ? (
                <>
                  <FooterLink to="/account">My account</FooterLink>
                  <FooterButton onClick={handleSignOut}>Log out</FooterButton>
                </>
              ) : (
                <FooterLink to="/account">My account</FooterLink>
              )}
            </FooterColumn>

            <FooterColumn title="Company">
              <FooterLink to="/support">Support</FooterLink>
              <FooterLink to="/become-a-creator">Become a creator</FooterLink>
            </FooterColumn>

            <FooterColumn title="Legal">
              <FooterLink to="/privacy">Privacy policy</FooterLink>
              <FooterLink to="/terms">Terms of service</FooterLink>
            </FooterColumn>
          </div>
        </div>

        <hr className="my-10 border-border" />

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {COPYRIGHT_NOTICE}
        </p>

        <p className="mt-6 text-[11px] text-muted-foreground">
          Copyright © RossCreations 2021-{year}. All Rights Reserved.
        </p>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
        {title}
      </h3>
      <ul className="mt-4 flex flex-col gap-3">{children}</ul>
    </div>
  );
}

function FooterLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-foreground transition-colors hover:text-muted-foreground"
      >
        {children}
      </Link>
    </li>
  );
}

function FooterButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="text-left text-sm text-foreground transition-colors hover:text-muted-foreground"
      >
        {children}
      </button>
    </li>
  );
}
